import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Eye,
    RefreshCw,
    Filter,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Store,
    Car,
    Home,
    CheckCircle,
    XCircle,
    Calendar,
    DollarSign,
    MapPin,
    User,
    Building2,
    CreditCard,
    Mail,
    Phone,
    Info,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
    useGetSoldListingsQuery,
    MarketplaceListing,
    SoldListingsParams
} from '../../store/slices/apiSlice';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';

const SoldListings: React.FC = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [listingTypeFilter, setListingTypeFilter] = useState<'all' | 'car' | 'house'>('all');
    const [listingSourceFilter, setListingSourceFilter] = useState<'all' | 'admin' | 'dealer'>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedListing, setSelectedListing] = useState<MarketplaceListing | null>(null);
    const [showPurchaseDetailsModal, setShowPurchaseDetailsModal] = useState(false);

    // Fetch sold listings - fetch all for client-side search and stats
    const { data, isLoading, error, refetch } = useGetSoldListingsQuery({
        page: 1,
        limit: 10000, // Fetch all for client-side filtering and stats
        listing_type: listingTypeFilter === 'all' ? undefined : listingTypeFilter,
        listing_source: listingSourceFilter === 'all' ? undefined : listingSourceFilter,
    });

    // Extract listings from API response
    const allListings = data?.data?.listings || [];
    const pagination = data?.data?.pagination || { current_page: 1, total_pages: 1, total_records: 0 };

    // Client-side search filtering
    const filteredListings = allListings.filter((listing: MarketplaceListing) => {
        const purchaser = (listing as any).purchaser;
        const matchesSearch =
            listing.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            listing.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            listing.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            listing.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
            purchaser?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            purchaser?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            purchaser?.mobile_number?.toLowerCase().includes(searchTerm.toLowerCase());
        
        return matchesSearch;
    });

    // Client-side pagination
    const totalPages = Math.ceil(filteredListings.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const listings = filteredListings.slice(startIndex, startIndex + itemsPerPage);

    // Calculate statistics
    const totalSold = allListings.length;
    const soldCars = allListings.filter(l => l.listing_type === 'car').length;
    const soldHouses = allListings.filter(l => l.listing_type === 'house').length;

    // Helper function to get page numbers for pagination
    const getPageNumbers = () => {
        const pages: number[] = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
            
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }
        
        return pages;
    };

    // Handle view - navigate to view page
    const handleView = (listing: MarketplaceListing) => {
        navigate(`/admin/marketplace-listings/view/${listing.listing_id}`);
    };

    // Format currency
    const formatCurrency = (amount: number, symbol: string | null = null) => {
        const currencySymbol = symbol || '₦';
        return `${currencySymbol}${amount.toLocaleString('en-US')}`;
    };

    // Format date
    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    // Format date with time
    const formatDateTime = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    // Handle view purchase details
    const handleViewPurchaseDetails = (listing: MarketplaceListing) => {
        setSelectedListing(listing);
        setShowPurchaseDetailsModal(true);
    };

    // Get listing type icon
    const getListingTypeIcon = (type: string) => {
        return type === 'car' ? <Car className="h-4 w-4" /> : <Home className="h-4 w-4" />;
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center">
                        <XCircle className="h-6 w-6 text-red-600 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-red-800">Error Loading Sold Listings</h3>
                            <p className="text-red-600 mt-1">There was an error loading the sold listings. Please try again.</p>
                            <button
                                onClick={() => refetch()}
                                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Sold Listings</h1>
                        <p className="text-gray-600">View all sold cars and houses from the marketplace</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => refetch()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-green-100">Total Sold Listings</p>
                            <p className="text-3xl font-bold text-white">{totalSold}</p>
                        </div>
                        <div className="p-3 bg-white/20 rounded-full">
                            <CheckCircle className="h-6 w-6 text-white" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-blue-100">Sold Cars</p>
                            <p className="text-3xl font-bold text-white">{soldCars}</p>
                        </div>
                        <div className="p-3 bg-white/20 rounded-full">
                            <Car className="h-6 w-6 text-white" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-purple-100">Sold Houses</p>
                            <p className="text-3xl font-bold text-white">{soldHouses}</p>
                        </div>
                        <div className="p-3 bg-white/20 rounded-full">
                            <Home className="h-6 w-6 text-white" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search by title, description, location, or tags..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <select
                                value={listingTypeFilter}
                                onChange={(e) => {
                                    setListingTypeFilter(e.target.value as any);
                                    setCurrentPage(1);
                                }}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Types</option>
                                <option value="car">Cars</option>
                                <option value="house">Houses</option>
                            </select>
                        </div>

                        <div className="relative">
                            <select
                                value={listingSourceFilter}
                                onChange={(e) => {
                                    setListingSourceFilter(e.target.value as any);
                                    setCurrentPage(1);
                                }}
                                className="pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Sources</option>
                                <option value="admin">Admin Listings</option>
                                <option value="dealer">Dealer Listings</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Listings Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Sold Listings ({filteredListings.length})
                        </h2>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Items per page:</label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>
                </div>

                {listings.length === 0 ? (
                    <div className="p-12 text-center">
                        <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No sold listings found</h3>
                        <p className="text-gray-600">No sold listings match your current filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purchaser</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purchase Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sold Date</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {listings.map((listing) => (
                                    <tr key={listing.listing_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="h-16 w-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                                {listing.images && listing.images.length > 0 ? (
                                                    <img
                                                        src={listing.images[0].image_path}
                                                        alt={listing.title}
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <Store className="h-6 w-6 text-gray-400" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">{listing.title}</div>
                                            <div className="text-sm text-gray-500 truncate max-w-xs">{listing.description}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                {getListingTypeIcon(listing.listing_type)}
                                                <span className="text-sm text-gray-900 capitalize">{listing.listing_type}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {formatCurrency(listing.price, listing.currency_symbol)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {listing.location ? (
                                                <div className="flex items-center gap-1 text-sm text-gray-600">
                                                    <MapPin className="h-3 w-3" />
                                                    {listing.location}
                                                </div>
                                            ) : (
                                                <span className="text-sm text-gray-400">N/A</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {listing.listing_source === 'dealer' && listing.dealer ? (
                                                <div className="flex items-center gap-2">
                                                    <User className="h-4 w-4 text-gray-400" />
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{listing.dealer.name}</div>
                                                        <div className="text-xs text-gray-500">{listing.dealer.business_name}</div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <Building2 className="h-4 w-4 text-gray-400" />
                                                    <span className="text-sm text-gray-600">Admin</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {(listing as any).purchaser ? (
                                                <div className="flex items-center gap-2">
                                                    <User className="h-4 w-4 text-gray-400" />
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{(listing as any).purchaser.name}</div>
                                                        <div className="text-xs text-gray-500">{(listing as any).purchaser.email}</div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-gray-400">N/A</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {(listing as any).purchase_details ? (
                                                <div>
                                                    <div className="text-sm font-semibold text-gray-900">
                                                        {formatCurrency((listing as any).purchase_details.amount, listing.currency_symbol)}
                                                    </div>
                                                    <div className="text-xs text-gray-500 capitalize">
                                                        {(listing as any).purchase_details.payment_method?.replace('_', ' ')}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-gray-400">N/A</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-1 text-sm text-gray-600">
                                                <Calendar className="h-3 w-3" />
                                                {(listing as any).purchase_details?.purchase_date 
                                                    ? formatDate((listing as any).purchase_details.purchase_date)
                                                    : formatDate(listing.updated_at)
                                                }
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                {(listing as any).purchaser && (
                                                    <button
                                                        onClick={() => handleViewPurchaseDetails(listing)}
                                                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors border border-transparent hover:border-gray-200"
                                                        title="View Purchase Details"
                                                    >
                                                        <Info className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleView(listing)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="View Listing Details"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                                <span className="font-medium">
                                    {Math.min(currentPage * itemsPerPage, filteredListings.length)}
                                </span>{' '}
                                of <span className="font-medium">{filteredListings.length}</span> listings
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    <ChevronsLeft className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                {getPageNumbers().map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`px-3 py-2 border rounded-lg ${
                                            currentPage === page
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    <ChevronsRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Purchase Details Modal */}
            <Modal
                isOpen={showPurchaseDetailsModal}
                onClose={() => {
                    setShowPurchaseDetailsModal(false);
                    setSelectedListing(null);
                }}
                title="Purchase Details"
            >
                {selectedListing && (selectedListing as any).purchaser && (selectedListing as any).purchase_details && (
                    <div className="p-6 space-y-6">
                        {/* Purchaser Information */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <User className="w-5 h-5 mr-2" />
                                Purchaser Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Name</label>
                                    <p className="mt-1 text-sm text-gray-900">{(selectedListing as any).purchaser.name}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Email</label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{(selectedListing as any).purchaser.email}</p>
                                    </div>
                                </div>
                                {(selectedListing as any).purchaser.mobile_number && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Mobile Number</label>
                                        <div className="mt-1 flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <p className="text-sm text-gray-900">{(selectedListing as any).purchaser.mobile_number}</p>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-medium text-gray-700">User ID</label>
                                    <p className="mt-1 text-sm text-gray-900">#{(selectedListing as any).purchaser.user_id}</p>
                                </div>
                            </div>
                        </div>

                        {/* Purchase Details */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <CreditCard className="w-5 h-5 mr-2" />
                                Purchase Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Transaction ID</label>
                                    <p className="mt-1 text-sm font-semibold text-gray-900">#{(selectedListing as any).purchase_details.transaction_id}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Purchase Date</label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">
                                            {formatDateTime((selectedListing as any).purchase_details.purchase_date)}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Payment Method</label>
                                    <p className="mt-1 text-sm text-gray-900 capitalize">
                                        {(selectedListing as any).purchase_details.payment_method?.replace('_', ' ') || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Purchase Amount</label>
                                    <p className="mt-1 text-lg font-semibold text-gray-900">
                                        {formatCurrency((selectedListing as any).purchase_details.amount, selectedListing.currency_symbol)}
                                    </p>
                                </div>
                                {(selectedListing as any).purchase_details.wallet_amount > 0 && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Wallet Amount Used</label>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {formatCurrency((selectedListing as any).purchase_details.wallet_amount, selectedListing.currency_symbol)}
                                        </p>
                                    </div>
                                )}
                                {(selectedListing as any).purchase_details.external_reference && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">External Reference</label>
                                        <p className="mt-1 text-sm text-gray-900">{(selectedListing as any).purchase_details.external_reference}</p>
                                    </div>
                                )}
                                {(selectedListing as any).purchase_details.verified_by && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Verified By</label>
                                        <p className="mt-1 text-sm text-gray-900">{(selectedListing as any).purchase_details.verified_by}</p>
                                    </div>
                                )}
                                {(selectedListing as any).purchase_details.verified_at && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Verified At</label>
                                        <div className="mt-1 flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-gray-400" />
                                            <p className="text-sm text-gray-900">
                                                {formatDateTime((selectedListing as any).purchase_details.verified_at)}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Listing Information */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <Store className="w-5 h-5 mr-2" />
                                Listing Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Listing Title</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedListing.title}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Listing ID</label>
                                    <p className="mt-1 text-sm text-gray-900">#{selectedListing.listing_id}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Type</label>
                                    <p className="mt-1 text-sm text-gray-900 capitalize">{selectedListing.listing_type}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Original Price</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {formatCurrency(selectedListing.price, selectedListing.currency_symbol)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default SoldListings;

